"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Footer — 2026-08-29 editorial redesign (owner: design it like the best,
 * not just lay it out). The pattern borrowed from premium product sites
 * (Stripe/Linear-class closings):
 *
 *  - DARK closing: ink background bookends the dark savings section and
 *    gives the page a deliberate final beat instead of trailing off in
 *    cream.
 *  - Brand block with a real tagline (a last restatement of the promise,
 *    not a URL), language switch beneath it.
 *  - THREE substantial link columns (Product+Contact, Guides, Legal) —
 *    the old four thin columns with a one-link "Company" read as
 *    scaffolding, not design.
 *  - Bottom bar: copyright + Made in Germany on the left, a circular
 *    back-to-top arrow on the right (one click, smooth scroll, instant
 *    for reduced-motion users).
 */

import { ArrowUp } from "lucide-react"
import { Link } from "react-router-dom"
import { LanguageToggle } from "@/components/layout/LanguageToggle"
import { useI18n } from "@/i18n"
import { BrandMarkV2 } from "./BrandMarkV2"
import { LEGAL_PATHS } from "@/lib/legalRoutes"
import { clearAllConsent } from "@/lib/consentGate"

const LINK_CLASS =
  "inline-flex min-h-9 items-center rounded-bb-sm text-left text-sm text-paper/60 transition-colors hover:text-paper focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime"

const HEADING_CLASS = "text-xs font-bold tracking-[0.14em] text-lime/80 uppercase"

function scrollToTop() {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" })
}

export function LandingFooter() {
  const { t } = useI18n()

  return (
    <footer className="overflow-hidden bg-ink text-paper">
      <div className="mx-auto grid max-w-6xl gap-x-8 gap-y-12 px-4 pt-16 pb-12 sm:grid-cols-2 md:px-8 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
        {/* ------------------------------------------------------- Brand */}
        <div className="flex flex-col gap-4 sm:col-span-2 lg:col-span-1">
          <div className="flex items-center gap-2.5">
            <BrandMarkV2 size={30} />
            <span className="font-display text-xl font-bold">{t("common.appName")}</span>
          </div>
          <p className="max-w-xs text-sm leading-relaxed text-paper/70">
            {t("landingPage.footer.tagline")}
          </p>
          <div className="mt-1 flex items-center gap-3">
            <span className="text-xs font-medium text-paper/50">
              {t("landingPage.footer.languageHeading")}
            </span>
            <LanguageToggle onDark />
          </div>
        </div>

        {/* ----------------------------------------------------- Product */}
        <nav aria-label={t("landingPage.footer.productHeading")} className="flex flex-col gap-3">
          <p className={HEADING_CLASS}>{t("landingPage.footer.productHeading")}</p>
          <a href="#how" className={LINK_CLASS}>
            {t("landingPage.header.navHow")}
          </a>
          <a href="#savings" className={LINK_CLASS}>
            {t("landingPage.header.navSavings")}
          </a>
          <a href="#pricing" className={LINK_CLASS}>
            {t("landingPage.footer.linkPricing")}
          </a>
          <a href="#employers" className={LINK_CLASS}>
            {t("landingPage.header.navEmployers")}
          </a>
          <a href="#contact" className={LINK_CLASS}>
            {t("landingPage.header.navContact")}
          </a>
        </nav>

        {/* ------------------------------------------------------ Guides */}
        <nav aria-label={t("landingPage.footer.guidesHeading")} className="flex flex-col gap-3">
          <p className={HEADING_CLASS}>{t("landingPage.footer.guidesHeading")}</p>
          <Link to="/guides/save-your-day" className={LINK_CLASS}>
            {t("guides.saveYourDay.hero.title")}
          </Link>
          <Link to="/guides/why-the-scale-lies" className={LINK_CLASS}>
            {t("guides.whyTheScaleLies.hero.title")}
          </Link>
          <Link to="/guides" className={LINK_CLASS}>
            {t("landingPage.footer.linkAllGuides")}
          </Link>
        </nav>

        {/* ------------------------------------------------------- Legal */}
        <nav aria-label={t("landingPage.footer.legalHeading")} className="flex flex-col gap-3">
          <p className={HEADING_CLASS}>{t("landingPage.footer.legalHeading")}</p>
          <Link to={LEGAL_PATHS.privacy} className={LINK_CLASS}>
            {t("landingPage.footer.privacyLink")}
          </Link>
          <Link to={LEGAL_PATHS.terms} className={LINK_CLASS}>
            {t("landingPage.footer.termsLink")}
          </Link>
          <Link to={LEGAL_PATHS.imprint} className={LINK_CLASS}>
            {t("landingPage.footer.imprintLink")}
          </Link>
          <a href="#trust" className={LINK_CLASS}>
            {t("landingPage.footer.dataPrivacyLink")}
          </a>
          {/* Cookie settings: forgets BOTH recorded choices (analytics + marketing), which re-opens the consent banner
              (components/ConsentBanner.tsx) — withdrawing is as easy as giving consent, also for a
              logged-out visitor (GDPR Art. 7(3)). */}
          <button type="button" className={LINK_CLASS} onClick={() => clearAllConsent()}>
            {t("consent.settingsLink")}
          </button>
        </nav>
      </div>

      {/* ---------------------------------------------------- Bottom bar */}
      <div className="border-t border-paper/10">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-5 md:px-8">
          <p className="text-xs text-paper/50">
            © {new Date().getFullYear()} {t("common.appName")} · {t("landingPage.footer.madeIn")}
          </p>
          <button
            type="button"
            aria-label={t("landingPage.footer.backToTop")}
            onClick={scrollToTop}
            className="group flex size-11 shrink-0 items-center justify-center rounded-full border border-paper/20 text-paper/70 transition-all duration-300 hover:border-lime hover:bg-lime hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime"
          >
            <ArrowUp
              className="size-5 transition-transform duration-300 group-hover:-translate-y-0.5"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </footer>
  )
}
